import { defineComponent as _defineComponent } from 'vue'
import { openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode } from "vue"

const _hoisted_1 = ["innerHTML"]

import { ref, watch, onMounted, nextTick } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute } from 'vue-router'
import MarkdownIt from 'markdown-it'
import DOMPurify from 'dompurify'
import matter from 'gray-matter'


export default /*@__PURE__*/_defineComponent({
  __name: 'MarkdownArticle',
  props: {
    articleName: {}
  },
  setup(__props: any) {

const props = __props

const { locale } = useI18n()
const route = useRoute()
const contentRef = ref<HTMLElement | null>(null)

// Configure MarkdownIt with anchor plugin
const md = new MarkdownIt({
  html: true,
  linkify: true,
  typographer: true
})

// Custom renderer for headings with IDs
const defaultHeadingOpen = md.renderer.rules.heading_open || function(tokens, idx, options, env, renderer) {
  return renderer.renderToken(tokens, idx, options)
}

md.renderer.rules.heading_open = function(tokens, idx, options, env, renderer) {
  const token = tokens[idx]
  const nextToken = tokens[idx + 1]
  
  if (nextToken && nextToken.type === 'inline') {
    // Generate ID from heading text
    const headingText = nextToken.content
    const headingId = headingText
      .toLowerCase()
      .replace(/[^\w\s\u0400-\u04FF-]/g, '') // Keep Latin and Cyrillic chars
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim()
    
    // Add id attribute
    token.attrSet('id', headingId)
  }
  
  return defaultHeadingOpen(tokens, idx, options, env, renderer)
}

const content = ref<string | null>(null)

const loadMarkdown = async (): Promise<void> => {
  try {
    const rawModule = await import(
      /* webpackChunkName: "content" */
      `@/assets/articles/${locale.value}/${props.articleName}.md`
    )

    // Парсим Front Matter и получаем чистый контент
    const { content: markdownContent } = matter(rawModule.default)

    // Рендерим только основной контент без Front Matter
    content.value = DOMPurify.sanitize(md.render(markdownContent), {
      ADD_ATTR: ['id'] // Allow id attributes for headings
    })
    
    // Add anchor links after content is rendered
    await nextTick()
    addAnchorLinks()
    
    // Scroll to hash if present
    if (route.hash) {
      scrollToHash(route.hash)
    }
  } catch (e) {
    content.value = null
  }
}

const addAnchorLinks = () => {
  if (!contentRef.value) return
  
  const headings = contentRef.value.querySelectorAll('h1[id], h2[id], h3[id], h4[id], h5[id], h6[id]')
  
  headings.forEach((heading) => {
    const id = heading.getAttribute('id')
    if (!id) return
    
    // Create anchor link element
    const anchor = document.createElement('a')
    anchor.className = 'anchor-link'
    anchor.href = `#${id}`
    anchor.setAttribute('aria-label', 'Copy link to section')
    anchor.innerHTML = `<svg class="anchor-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
      <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71"></path>
      <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71"></path>
    </svg>`
    
    // Add click handler to copy link
    anchor.addEventListener('click', (e) => {
      e.preventDefault()
      const url = new URL(window.location.href)
      url.hash = id
      navigator.clipboard.writeText(url.toString())
      
      // Show copied feedback
      anchor.classList.add('copied')
      setTimeout(() => {
        anchor.classList.remove('copied')
      }, 2000)
      
      // Smooth scroll to heading
      heading.scrollIntoView({ behavior: 'smooth' })
    })
    
    // Make heading a flex container and prepend anchor (at the beginning)
    heading.classList.add('heading-with-anchor')
    heading.insertBefore(anchor, heading.firstChild)
  })
}

const scrollToHash = (hash: string) => {
  const id = hash.slice(1) // Remove #
  const element = document.getElementById(id)
  if (element) {
    setTimeout(() => {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' })
    }, 100)
  }
}

watch([locale, () => props.articleName], loadMarkdown)

// Watch for hash changes
watch(() => route.hash, (newHash) => {
  if (newHash) {
    scrollToHash(newHash)
  }
})

onMounted(loadMarkdown)

return (_ctx: any,_cache: any) => {
  return (content.value)
    ? (_openBlock(), _createElementBlock("div", {
        key: 0,
        class: "markdown-content",
        ref_key: "contentRef",
        ref: contentRef,
        innerHTML: content.value
      }, null, 8, _hoisted_1))
    : _createCommentVNode("", true)
}
}

})